// src/components/PatronManagementView/PatronManagementView.js
import React from 'react';
import PropTypes from 'prop-types';
import AppHeader from '../AppHeader';
import PatronManagement from '../../PatronManagement';
import './PatronManagementView.css';

/**
 * PatronManagementView - Wrapper component for patron management view
 * 
 * Provides consistent layout with header, navigation, and PatronManagement component.
 * Handles view switching back to device management.
 */
const PatronManagementView = ({
  // Header props
  theme,
  activeLogo,
  onToggleLogo,
  organizationId,
  organizationName,
  installationId,
  installationName,
  orgInstallContext,
  // Hamburger menu props
  onNavigateToPatrons,
  onNavigateToDevices,
  onOpenAdvancedSettings,
  onOpenInstallationActions,
  onOpenInstallationManagement,
  onOpenOrganizationManagement,
  onToggleDevStreams,
  deviceCount,
  devices,
  onDevicesClaimed,
  allDeviceStatesLoaded,
  currentOrgSettings,
  // Patron management props
  organizations,
  pendingDeletionPatrons,
  setPendingDeletionPatrons,
  pendingDeletionCredentials,
  setPendingDeletionCredentials,
  pendingAdditionCredentials,
  setPendingAdditionCredentials,
  pendingFullCredentialDeletions,
  setPendingFullCredentialDeletions,
  devStreamEvents,
  lastCredentialDownload,
  lastCredentialProvisioned,
  lastCredentialRemoved,
}) => {
  return (
    <>
      <AppHeader
        currentView="patrons"
        title="Patron Management"
        theme={theme}
        activeLogo={activeLogo}
        onToggleLogo={onToggleLogo}
        organizationId={organizationId}
        organizationName={organizationName}
        installationId={installationId}
        installationName={installationName}
        orgInstallContext={orgInstallContext}
        onNavigateToPatrons={onNavigateToPatrons}
        onNavigateToDevices={onNavigateToDevices}
        onOpenAdvancedSettings={onOpenAdvancedSettings}
        onOpenInstallationActions={onOpenInstallationActions}
        onOpenInstallationManagement={onOpenInstallationManagement}
        onOpenOrganizationManagement={onOpenOrganizationManagement}
        onToggleDevStreams={onToggleDevStreams}
        deviceCount={deviceCount}
        devices={devices}
        onDevicesClaimed={onDevicesClaimed}
        allDeviceStatesLoaded={allDeviceStatesLoaded}
        currentOrgSettings={currentOrgSettings}
      />
      
      <div className="context-bar"></div>
      
      <div className="view-switcher">
        <button
          className="view-switch-button"
          onClick={onNavigateToDevices}
        >
          ← Back to Device Management
        </button>
      </div>
      
      <PatronManagement
        organizationId={organizationId}
        organizationName={organizationName}
        organizations={organizations}
        devices={devices}
        pendingDeletionPatrons={pendingDeletionPatrons}
        setPendingDeletionPatrons={setPendingDeletionPatrons}
        pendingDeletionCredentials={pendingDeletionCredentials}
        setPendingDeletionCredentials={setPendingDeletionCredentials}
        pendingAdditionCredentials={pendingAdditionCredentials}
        setPendingAdditionCredentials={setPendingAdditionCredentials}
        pendingFullCredentialDeletions={pendingFullCredentialDeletions}
        setPendingFullCredentialDeletions={setPendingFullCredentialDeletions}
        devStreamEvents={devStreamEvents}
        lastCredentialDownload={lastCredentialDownload}
        lastCredentialProvisioned={lastCredentialProvisioned}
        lastCredentialRemoved={lastCredentialRemoved}
      />
    </>
  );
};

PatronManagementView.propTypes = {
  // Header props
  theme: PropTypes.string.isRequired,
  activeLogo: PropTypes.string,
  onToggleLogo: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
  organizationName: PropTypes.string,
  installationId: PropTypes.string,
  installationName: PropTypes.string,
  orgInstallContext: PropTypes.object,
  // Hamburger menu props
  onNavigateToPatrons: PropTypes.func.isRequired,
  onNavigateToDevices: PropTypes.func.isRequired,
  onOpenAdvancedSettings: PropTypes.func.isRequired,
  onOpenInstallationActions: PropTypes.func.isRequired,
  onOpenInstallationManagement: PropTypes.func.isRequired,
  onOpenOrganizationManagement: PropTypes.func.isRequired,
  onToggleDevStreams: PropTypes.func.isRequired,
  deviceCount: PropTypes.number,
  devices: PropTypes.array,
  onDevicesClaimed: PropTypes.func.isRequired,
  allDeviceStatesLoaded: PropTypes.bool,
  currentOrgSettings: PropTypes.object,
  // Patron management props
  organizations: PropTypes.array,
  pendingDeletionPatrons: PropTypes.instanceOf(Set),
  setPendingDeletionPatrons: PropTypes.func.isRequired,
  pendingDeletionCredentials: PropTypes.instanceOf(Set),
  setPendingDeletionCredentials: PropTypes.func.isRequired,
  pendingAdditionCredentials: PropTypes.instanceOf(Set),
  setPendingAdditionCredentials: PropTypes.func.isRequired,
  pendingFullCredentialDeletions: PropTypes.instanceOf(Set),
  setPendingFullCredentialDeletions: PropTypes.func.isRequired,
  devStreamEvents: PropTypes.array,
  lastCredentialDownload: PropTypes.object,
  lastCredentialProvisioned: PropTypes.object,
  lastCredentialRemoved: PropTypes.object,
};

export default PatronManagementView;
